/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Auth Hooks - React Query Mutations for Authentication
 * ============================================================================
 *
 * Custom hooks for authentication operations using React Query.
 *
 * ============================================================================
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/services/auth.service';
import { useAuthStore } from '@/stores/authStore';
import {
  LoginRequest,
  AuthenticationResult,
  MfaVerifyRequest,
  MfaConfirmRequest,
  PasswordResetRequestPayload,
  PasswordResetPayload,
  MfaForResetRequest,
  VerifyResetTokenRequest,
  ChangePasswordRequest,
  ValidatePasswordRequest,
  VerifyForRecoveryRequest,
  BootstrapRequest,
  BootstrapStatus,
  BootstrapResult,
  MfaSetupVerifyIdentityRequest,
  MfaSetupVerifyIdentityResponse,
  MfaSetupInitTotpRequest,
  MfaSetupCompleteRequest,
  MfaSetupResponse,
  AcceptInviteRequest,
} from '@/types/auth.types';
import type { UserRole } from '@/types/auth.types';
import { ApiError } from '@/services/api/types';

/**
 * Returns the default landing page based on user role.
 * SUPER_ADMIN goes to the platform admin dashboard.
 * All org-level users go to the VENTA module.
 */
function getDefaultRoute(role: UserRole): string {
  return role === 'SUPER_ADMIN' ? '/superadmin' : '/venta';
}

// ============================================================================
// LOGIN
// ============================================================================

/**
 * Hook for login mutation
 */
export function useLogin() {
  const navigate = useNavigate();
  const { login: storeLogin } = useAuthStore();

  return useMutation<AuthenticationResult, ApiError, LoginRequest>({
    mutationFn: authService.login,
    onSuccess: (data) => {
      if (data.mfaSetupRequired) {
        // Redirect to MFA setup page (enforced MFA but not yet configured)
        navigate('/auth/mfa/setup-required', {
          state: {
            mfaToken: data.mfaToken,
            userEmail: data.userEmail,
            availableSetupMethods: data.availableSetupMethods,
          },
        });
      } else if (data.mfaRequired) {
        // Redirect to MFA verification (already has MFA configured)
        navigate('/auth/mfa/verify', {
          state: {
            mfaToken: data.mfaToken,
            mfaMethods: data.mfaMethods,
          },
        });
      } else if (data.tokens && data.user) {
        // Direct login without MFA
        storeLogin(data.user, data.tokens.accessToken, data.tokens.refreshToken);
        navigate(getDefaultRoute(data.user.role));
      }
    },
  });
}

// ============================================================================
// MFA VERIFICATION
// ============================================================================

/**
 * Hook for MFA verification mutation
 */
export function useMfaVerify() {
  const navigate = useNavigate();
  const { login: storeLogin } = useAuthStore();

  return useMutation<AuthenticationResult, ApiError, MfaVerifyRequest>({
    mutationFn: authService.verifyMfa,
    onSuccess: (data) => {
      if (data.tokens && data.user) {
        storeLogin(data.user, data.tokens.accessToken, data.tokens.refreshToken);
        navigate(getDefaultRoute(data.user.role));
      }
    },
  });
}

// ============================================================================
// MFA SETUP
// ============================================================================

/**
 * Hook for initializing TOTP setup
 */
export function useMfaSetup() {
  return useMutation({
    mutationFn: authService.initializeTotpSetup,
  });
}

/**
 * Hook for verifying TOTP setup.
 * Does NOT navigate — caller (MFASetupPage) handles navigation after showing recovery codes.
 * Updates mfaEnabled in auth store on success.
 */
export function useMfaSetupVerify() {
  const { user, login: storeLogin, accessToken, refreshToken } = useAuthStore();

  return useMutation<unknown, ApiError, MfaConfirmRequest>({
    mutationFn: authService.verifyTotpSetup,
    onSuccess: () => {
      // Update user's mfaEnabled flag in auth store
      if (user && accessToken && refreshToken) {
        storeLogin({ ...user, mfaEnabled: true }, accessToken, refreshToken);
      }
    },
  });
}

/**
 * Hook for sending email OTP during MFA verification flow
 * @param isSetup If true, skip cooldown timer (for initial MFA setup)
 */
export function useSendEmailOtp() {
  return useMutation<void, ApiError, { mfaToken: string; isSetup?: boolean }>({
    mutationFn: ({ mfaToken, isSetup }) => authService.sendEmailOtp(mfaToken, isSetup),
  });
}

/**
 * Hook for regenerating recovery codes
 */
export function useRegenerateRecoveryCodes() {
  return useMutation<unknown, ApiError, VerifyForRecoveryRequest>({
    mutationFn: authService.regenerateRecoveryCodes,
  });
}

// ============================================================================
// MFA SETUP DURING LOGIN (for enforced MFA users)
// ============================================================================

/**
 * Hook for verifying identity with Email OTP before MFA setup
 */
export function useVerifyIdentityForMfaSetup() {
  return useMutation<MfaSetupVerifyIdentityResponse, ApiError, MfaSetupVerifyIdentityRequest>({
    mutationFn: authService.verifyIdentityForMfaSetup,
  });
}

/**
 * Hook for initializing TOTP during MFA setup flow (after identity verification)
 */
export function useInitTotpForMfaSetup() {
  return useMutation<MfaSetupResponse, ApiError, MfaSetupInitTotpRequest>({
    mutationFn: authService.initTotpForMfaSetup,
  });
}

/**
 * Hook for completing MFA setup and getting authentication tokens
 */
export function useCompleteMfaSetup() {
  const navigate = useNavigate();
  const { login: storeLogin } = useAuthStore();

  return useMutation<AuthenticationResult, ApiError, MfaSetupCompleteRequest>({
    mutationFn: authService.completeMfaSetup,
    onSuccess: (data) => {
      if (data.tokens && data.user) {
        storeLogin(data.user, data.tokens.accessToken, data.tokens.refreshToken);
        navigate(getDefaultRoute(data.user.role));
      }
    },
  });
}

// ============================================================================
// PASSWORD RESET
// ============================================================================

/**
 * Hook for requesting password reset
 */
export function useRequestPasswordReset() {
  return useMutation<unknown, ApiError, PasswordResetRequestPayload>({
    mutationFn: authService.requestPasswordReset,
  });
}

/**
 * Hook for verifying reset token
 */
export function useVerifyResetToken() {
  return useMutation<
    import('@/types/auth.types').PasswordResetValidateResponse,
    ApiError,
    VerifyResetTokenRequest
  >({
    mutationFn: authService.verifyResetToken,
  });
}

/**
 * Hook for MFA verification during password reset
 */
export function useMfaForReset() {
  return useMutation<
    import('@/types/auth.types').MfaForResetResponse,
    ApiError,
    MfaForResetRequest
  >({
    mutationFn: authService.verifyMfaForReset,
  });
}

/**
 * Hook for completing password reset
 */
export function useCompletePasswordReset() {
  const navigate = useNavigate();

  return useMutation<unknown, ApiError, PasswordResetPayload>({
    mutationFn: authService.completePasswordReset,
    onSuccess: () => {
      navigate('/auth/login', {
        state: { message: 'Password reset successful. Please log in with your new password.' },
      });
    },
  });
}

/**
 * Hook for changing password (logged-in user)
 */
export function useChangePassword() {
  return useMutation<unknown, ApiError, ChangePasswordRequest>({
    mutationFn: authService.changePassword,
  });
}

/**
 * Hook for validating password strength
 */
export function useValidatePassword() {
  return useMutation<unknown, ApiError, ValidatePasswordRequest>({
    mutationFn: authService.validatePassword,
  });
}

// ============================================================================
// LOGOUT
// ============================================================================

/**
 * Hook for logout mutation
 */
export function useLogout() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { logout: storeLogout } = useAuthStore();

  return useMutation({
    mutationFn: authService.logout,
    onSuccess: () => {
      storeLogout();
      queryClient.clear();
      navigate('/auth/login');
    },
    onError: () => {
      // Even if API fails, clear local state
      storeLogout();
      queryClient.clear();
      navigate('/auth/login');
    },
  });
}

/**
 * Hook for logout all sessions
 */
export function useLogoutAll() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { logout: storeLogout } = useAuthStore();

  return useMutation({
    mutationFn: authService.logoutAll,
    onSuccess: () => {
      storeLogout();
      queryClient.clear();
      navigate('/auth/login');
    },
  });
}

// ============================================================================
// TOKEN REFRESH
// ============================================================================

/**
 * Hook for manual token refresh (usually handled automatically by interceptor)
 */
export function useRefreshToken() {
  const { setTokens } = useAuthStore();

  return useMutation({
    mutationFn: authService.refreshToken,
    onSuccess: (data) => {
      setTokens(data.accessToken, data.refreshToken);
    },
  });
}

// ============================================================================
// BOOTSTRAP
// ============================================================================

/**
 * Hook for checking bootstrap status
 */
export function useBootstrapStatus() {
  return useQuery<BootstrapStatus, ApiError>({
    queryKey: ['bootstrap', 'status'],
    queryFn: authService.getBootstrapStatus,
    retry: false,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook for bootstrapping the SuperAdmin
 */
export function useBootstrap() {
  const navigate = useNavigate();

  return useMutation<BootstrapResult, ApiError, BootstrapRequest>({
    mutationFn: authService.bootstrap,
    onSuccess: () => {
      // Redirect to login after successful bootstrap
      navigate('/auth/login', {
        state: { message: 'SuperAdmin created successfully. Please log in.' },
      });
    },
  });
}

// ============================================================================
// ACCEPT INVITATION
// ============================================================================

/**
 * Hook for accepting organization invitation
 */
export function useAcceptInvite() {
  const navigate = useNavigate();
  const { login: storeLogin } = useAuthStore();

  return useMutation<AuthenticationResult, ApiError, AcceptInviteRequest>({
    mutationFn: authService.acceptInvitation,
    onSuccess: (data) => {
      if (data.mfaSetupRequired) {
        // Redirect to MFA setup page (enforced MFA but not yet configured)
        navigate('/auth/mfa/setup-required', {
          state: {
            mfaToken: data.mfaToken,
            userEmail: data.userEmail,
            availableSetupMethods: data.availableSetupMethods,
          },
        });
      } else if (data.mfaRequired) {
        // Redirect to MFA verification (already has MFA configured)
        navigate('/auth/mfa/verify', {
          state: {
            mfaToken: data.mfaToken,
            mfaMethods: data.mfaMethods,
          },
        });
      } else if (data.tokens && data.user) {
        // Direct login without MFA — recommend MFA setup
        storeLogin(data.user, data.tokens.accessToken, data.tokens.refreshToken);
        navigate('/auth/mfa/recommend');
      }
    },
  });
}
